import React from 'react';
import { FieldNotify } from 'global';
import { Controller, RegisterOptions } from 'react-hook-form';
import { Control } from 'react-hook-form/dist/types/form';
import { FieldValues } from 'react-hook-form/dist/types/fields';
import { Box, TextField, TextFieldProps } from '@mui/material';
import { UseControllerProps } from 'react-hook-form/dist/types/controller';
import NumberFormat, { NumberFormatValues, SourceInfo } from 'react-number-format';
import { FormNotificationBlock } from '../FormNotificationBlock';

interface Props<FormValues extends FieldValues = FieldValues> {
  label?: string;
  max?: number;
  range?: [number | undefined, number | undefined];
  notify?: FieldNotify;
  allowNegative?: boolean;
  control: Control<FormValues>;
  textFieldProps?: TextFieldProps;
  name: UseControllerProps<FormValues>['name'];
  rules?: Omit<RegisterOptions<FormValues>, 'valueAsNumber' | 'valueAsDate' | 'setValueAs' | 'disabled'>;
  customOnChange?: (nextValue: number, name: UseControllerProps<FormValues>['name']) => void;
}

export const CurrencyInputField = <FormValues extends FieldValues = FieldValues>({
  name,
  max,
  range,
  label,
  notify,
  control,
  rules,
  allowNegative = false,
  customOnChange,
  textFieldProps = {},
}: Props<FormValues>) => {
  const handleChange = (onFieldChange: any, { value }: NumberFormatValues, sourceInfo: SourceInfo) => {
    let parsedValue = parseFloat(value);

    if (!isNaN(parsedValue) && range) {
      if (range[1] !== undefined && parsedValue > range[1]) parsedValue = range[1];
      if (range[0] !== undefined && parsedValue < range[0]) parsedValue = range[0];
    }

    if (sourceInfo.source === 'event') {
      if (customOnChange) {
        customOnChange(isNaN(parsedValue) ? 0 : parsedValue, name);
      } else {
        if (isNaN(parsedValue)) return onFieldChange(null);
        onFieldChange(parsedValue);
      }
    } else {
      onFieldChange(isNaN(parsedValue) ? null : parsedValue);
    }
  };

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', width: '100%' }}>
      <Controller
        name={name}
        rules={rules}
        control={control}
        render={({ field: { ref, value, onChange, onBlur, ...field }, fieldState }) => (
          <NumberFormat
            prefix="$"
            fullWidth
            size="small"
            label={label}
            inputRef={ref}
            decimalScale={2}
            thousandSeparator
            fixedDecimalScale
            variant="standard"
            allowNegative={allowNegative}
            customInput={TextField}
            {...field}
            {...(textFieldProps as any)}
            error={!!fieldState.error}
            helperText={fieldState.error ? fieldState.error.message : ''}
            value={parseFloat(value)}
            onValueChange={(values, sourceInfo) => handleChange(onChange, values, sourceInfo)}
            isAllowed={values => {
              const { floatValue } = values;
              if (max && floatValue && floatValue > max) {
                return false;
              }
              return true;
            }}
            onBlur={e => {
              onBlur();
              textFieldProps?.onBlur?.(e);
            }}
          />
        )}
      />

      {notify && <FormNotificationBlock {...notify} />}
    </Box>
  );
};
